import { z } from 'zod';

/**
 * A partner's share of an outlet's full collection, with no floating point
 * anywhere in the middle.
 *
 * The API carries it as `rateMilliPercent`, an **integer of thousandths of a
 * percent** — 24.2% is 24200 — for the reason `lib/money.ts` carries paise:
 * `16.1 * 1000` is `16100.000000000002`. A person still types a percentage,
 * so the digits are moved as text, the same way `paiseFromRupees` moves them,
 * and the arithmetic only ever runs on integers.
 */

/**
 * What a person may type: up to three whole digits and up to three decimals.
 * No sign, no `%` (it is on the field), no comma, no exponent. A fourth decimal
 * is refused rather than rounded, because a rounded rate is one nobody chose.
 */
const PERCENT_SHAPE = /^\d{1,3}(\.\d{1,3})?$/;

/** The published bounds on `rateMilliPercent`: `minimum: 1`, `maximum: 100000`. */
const MIN_MILLI_PERCENT = 1;
const MAX_MILLI_PERCENT = 100_000;

/**
 * A percentage as typed, as an integer of `10^-places` of a percent — the one
 * digit-moving rule, shared with the GST rate's basis points
 * (`catalogue/tax.ts`, two places), which is the same shape at another scale.
 *
 * **Assumes the caller's shape has matched**, with at most `places` decimals.
 * The fraction is padded on the right, so `12.5` is 12500 and `12.005` is 12005
 * at three places — the end the padding goes on is the difference between the
 * two.
 */
export function percentUnitsFrom(percent: string, places: number): number {
  const [whole = '', fraction = ''] = percent.trim().split('.');

  return Number(whole) * 10 ** places + Number(fraction.padEnd(places, '0'));
}

/**
 * The inverse: `10^-places` of a percent as somebody reads it. Trailing zeros
 * go; the digits are split, never divided.
 */
export function formatPercentUnits(units: number, places: number): string {
  const digits = String(Math.round(units)).padStart(places + 1, '0');
  const fraction = digits.slice(-places).replace(/0+$/, '');

  return `${digits.slice(0, -places)}${fraction === '' ? '' : `.${fraction}`}%`;
}

/** A percentage as typed, in thousandths of one. Assumes `PERCENT_SHAPE` has matched. */
export const milliPercentFrom = (percent: string): number => percentUnitsFrom(percent, 3);

/** Thousandths of a percent as somebody reads it: 24200 is `24.2%`, 1 is `0.001%`. */
export const formatRate = (milliPercent: number): string => formatPercentUnits(milliPercent, 3);

export const RATE_REQUIRED = 'Enter the partner’s percentage of the collection.';

export const RATE_MALFORMED =
  'A rate is a percentage with up to three decimal places — 24.2 or 30.425. No % sign and no minus.';

export const RATE_OUT_OF_RANGE = 'A rate is more than 0% and at most 100%.';

/**
 * The rate field. Held as text, so "not answered", "not a percentage" and "out
 * of range" stay three sentences; the form converts with `milliPercentFrom` on
 * the way out, and nothing else ever holds the rate as a number.
 */
export const rateSchema = z
  .string()
  .trim()
  .min(1, RATE_REQUIRED)
  .regex(PERCENT_SHAPE, RATE_MALFORMED)
  .refine((percent) => {
    const milli = milliPercentFrom(percent);

    return milli >= MIN_MILLI_PERCENT && milli <= MAX_MILLI_PERCENT;
  }, RATE_OUT_OF_RANGE);
